//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import {
  ItemInfo,
  ProductInfo,
  UnifiedMetricsItemProductStatsResponse,
} from '@bvengineering/metrics-gateway-service-types';

import { getItemDetails } from '@apis/content-library/contentLibraryApi';
import * as metricsApi from '@apis/metrics/metricsApi';
import { IItemProductStatsRequest } from '@apis/metrics/metricsApiModel';
import { TopPerformersContentType } from '@insights/insightsConstants';
import {
  IInsightsActivityStats,
  TopPerformersItemProductStat,
} from '@insights/redux/insightsPageModel';
import Cuid from '@models/Cuid';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { UniversalItemType } from '@models/UniversalItemType';
import { isDefined } from '@utils/filterPredicates';
import { itemToPartialCuid } from '@utils/partialCuidUtils';
import getUrlParams from '@utils/urlUtils';

const getCuid = (
  contentType: TopPerformersContentType,
  item: UnifiedMetricsItemProductStatsResponse,
) => {
  switch (contentType) {
    case TopPerformersContentType.PRODUCT:
      return itemToPartialCuid(
        UniversalItemType.Product,
        ItemId((item.mediaInfo as ProductInfo).fileId ?? ''),
      );
    case TopPerformersContentType.MEDIA:
    default:
      return PartialCuid.build({
        itemType: Cuid.parse((item.mediaInfo as ItemInfo).cuid).itemType,
        id: Cuid.parse((item.mediaInfo as ItemInfo).cuid).itemId,
      });
  }
};

export const fetchApiActivityStats = async (
  contentType: TopPerformersContentType,
  itemProductStatsRequest: IItemProductStatsRequest,
  activityStats: IInsightsActivityStats,
): Promise<IInsightsActivityStats> => {
  let itemStatsRes;
  switch (contentType) {
    case TopPerformersContentType.MEDIA: {
      itemStatsRes = await metricsApi.getItemStats(itemProductStatsRequest);
      break;
    }
    case TopPerformersContentType.CONTRIBUTORS: {
      itemStatsRes = await metricsApi.getContributorStats(
        itemProductStatsRequest,
      );
      break;
    }
    case TopPerformersContentType.SHOWROOM: {
      itemStatsRes = await metricsApi.getSourceStats(itemProductStatsRequest);
      break;
    }
    case TopPerformersContentType.REVEALS: {
      itemStatsRes = await metricsApi.getItemSourceStats(
        itemProductStatsRequest,
      );
      break;
    }
    case TopPerformersContentType.PRODUCT:
    default: {
      itemStatsRes = await metricsApi.getProductStats(itemProductStatsRequest);
      break;
    }
  }

  // Fetch Item Details and build result
  const partialcuids =
    contentType === TopPerformersContentType.CONTRIBUTORS ||
    contentType === TopPerformersContentType.PRODUCT
      ? []
      : itemStatsRes.map((item) => getCuid(contentType, item));

  const itemDetailsRes =
    partialcuids.length > 0 ? await getItemDetails(partialcuids) : undefined;

  const itemStatsWithDetails = itemStatsRes.map((item, index) => {
    const partialCuid = partialcuids[index];

    return {
      stats: item,
      details: isDefined(partialCuid)
        ? itemDetailsRes?.[partialCuid]
        : undefined,
    };
  });

  return {
    ...activityStats,
    itemStats: itemStatsWithDetails,
  };
};

export const getTestVariationFromParams = () => {
  const testVariationParam = getUrlParams().testVariation;
  return isDefined(testVariationParam)
    ? decodeURIComponent(testVariationParam)
    : undefined;
};
